import { fontFaceGenerator, generateFontUid } from '../Font/fontFaceGenerator';
import IPostMessage from '../IPostMessage';
import validate from '../Validate/Validate';
import Cache from './Cache/Cache';
import IFile from './Cache/IFile';
import AppendFlagController from './Flags/AppendFlagController';
import IFlag from './Flags/IFlag';
import IFlags from './Flags/IFlags';
import { IAddFont, IFontFormats, VIAddFont } from './IAddFont';
import IFileType from './IFileType';
import ISaveFile, { VISaveFile } from './ISaveFile';
import CssLoadFileController from './LoadFile/CssLoadFileController';
import ILoadFile from './LoadFile/ILoadFile';
import { ILoadFileControllerStatic } from './LoadFile/ILoadFileController';
import JavascriptLoadFileController from './LoadFile/JavascriptLoadFileController';
import Types, { ITypes } from './Types';

type FlagControllers = {
	append: AppendFlagController;
};

/**
 * Web-based applications often require additional assets to function, but downloading them every time is undesirable. The JS Applet SDK offers a wide range of APIs for storing different kinds of assets.
 *
 * Generally, there are four types of assets:
 * - CSS & JavaScript files
 * - Fonts
 * - Audio-visual content (image, video, and audio files)
 * - Arbitrary data (e.g., JSON files, plain text, binary data)
 *
 * And the SDK also provides two different APIs:
 * - `sos.offline` & `sos.offline.cache`: A file system abstraction for caching files that are removed when the applet configuration is changed or the applet is reloaded. This method is usually preferred if the files can be re-downloaded.
 * - `sos.fileSystem`: Direct access to the file system. All files are stored permanently and are not removed when the applet configuration changes.
 *
 * Using the `sos.offline` and `sos.offline.cache` APIs allows you to ship an applet without content, including only basic bootstrapping code, and load the content dynamically. The content will then be available even when the device is offline.
 *
 * :::warning
 * Emulator has certain limitations while handling offline files. [Read more here](https://docs.signageos.io/hc/en-us/articles/4405238997138)
 * :::
 */
export default class Offline {
	public static MESSAGE_PREFIX: string = 'offline';

	public readonly cache: Cache;
	public readonly types: ITypes;
	public readonly loadFile: ILoadFile;
	public readonly flags: IFlags<FlagControllers>;
	private readonly flagControllers: FlagControllers;
	private FlagControllers: [typeof AppendFlagController] = [AppendFlagController];
	private LoadFileControllers: ILoadFileControllerStatic[] = [JavascriptLoadFileController, CssLoadFileController];

	/** @internal */
	constructor(
		private messagePrefix: string,
		private window: Window,
		private postMessage: IPostMessage<any>,
	) {
		this.cache = new Cache(this.getMessagePrefix(), this.postMessage);
		this.types = Types;
		this.loadFile = this.LoadFileControllers.reduce((loadFile: ILoadFile, LoadFileController: ILoadFileControllerStatic) => {
			const loadFileController = new LoadFileController(this.window);
			loadFile[loadFileController.name] = loadFileController;
			return loadFile;
		}, {});
		this.flagControllers = this.FlagControllers.reduce((flagControllers, FlagController) => {
			const flagController = new FlagController(this.loadFile);
			flagControllers[flagController.name] = flagController;
			return flagControllers;
		}, {} as FlagControllers);
		this.flags = (Object.keys(this.flagControllers) as (keyof FlagControllers)[]).reduce(
			(flags, flagControllerName) => {
				const flagController = this.flagControllers[flagControllerName]!;
				flags[flagController.name] = flagController.create.bind(flagController);
				return flags;
			},
			{
				append: null as never,
			} as IFlags<FlagControllers>,
		);
	}

	/**
	 * The `addFiles()` method downloads the specified files and stores them locally.
	 * The order in which the files are downloaded and stored is **not** guaranteed.
	 *
	 * CSS and JavaScript files are specific because they must be loaded with an HTML tag. The `sos.offline` API methods download, save, and append the resource to the web page. When adding a file, choose a unique identifier (`uid`) that should not change for the resource. The file will be overwritten if another file is added with the same `uid`.
	 *
	 * @param files Array of files to be downloaded and stored locally.
	 * @returns {Promise<Awaited<void>>} Resolves when all files are added.
	 * @throws {Error} If the `files` parameter is not an array of objects.
	 * @since 1.0.0
	 *
	 * @example // {@link https://github.com/signageos/applet-examples/blob/master/examples/content-js-api/offline-resources | Example of Applet that save files and load them in one file}
	 * @example
	 * await sos.offline.addFile([{
	 * 	uri: 'https://unpkg.com/normalize.css@8.0.1/normalize.css',
	 * 	uid: 'normalize.css',
	 * 	type: sos.offline.types.css,
	 * 	flags: [sos.offline.flags.append(document.head)],
	 * }, {
	 * 	uri: 'https://code.jquery.com/jquery-3.7.1.slim.min.js',
	 * 	uid: 'jquery-3.7.1.slim.min.js',
	 * 	type: sos.offline.types.javascript,
	 * 	flags: [sos.offline.flags.append(document.body)],
	 * }]);
	 */
	public addFiles(files: ISaveFile[]): Promise<Awaited<void>[]> {
		validate({ addFiles: files }).required().array('object');
		const promises = files.map((addFile: ISaveFile) => this.addFile(addFile));
		return Promise.all(promises);
	}

	/**
	 * Method `addFile()` will allow you to load single resource into applet. If you want to load more resource, use `addFiles()`.
	 *
	 * The order in which the files are downloaded and stored **is** guaranteed.
	 *
	 * @param files Array of files to be downloaded and stored locally.
	 * @returns {Promise<void>} Resolves when all files are added and the files are appended to the document when flags are used.
	 * @throws {Error} If the `files` parameter is not an array of objects
	 * @since 1.0.0
	 *
	 * @example
	 * const file = { // File that will be loaded into an applet
	 *     "uri": "https://ajax.googleapis.com/ajax/libs/jquery/2.2.4/jquery.min.js",
	 *     "uid": "jquery-2.2.4.min.js",
	 *     "type": sos.offline.types.javascript,
	 *     "flags": [sos.offline.flags.append(document.body)]
	 * }
	 *
	 * await sos.offline.addFile(file); // And finally load file
	 */
	public async addFilesSync(files: ISaveFile[]): Promise<void> {
		validate({ addFiles: files }).required().array('object');

		for (const file of files) {
			await this.addFile(file);
		}
	}

	/**
	 * The `addFile()` method downloads the specified file and stores it locally.
	 *
	 * CSS and JavaScript files are specific because they must be loaded with an HTML tag. The `sos.offline` API methods download, save, and append the resource to the web page. When adding a file, choose a unique identifier (`uid`) that should not change for the resource. The file will be overwritten if another file is added with the same `uid`.
	 *
	 * :::info
	 * The file URL must point to a file. If your URI leads to a redirect (e.g. from http to https), the API will not work.
	 * :::
	 *
	 * @param file URI of the file to be downloaded.
	 * @param file.uid Unique file identifier is used for later file retrieval, must contain a-z,A-Z,0-9 characters.
	 * @param file.type Type of the file, e.g. 'javascript', 'css', etc.
	 * @param file.headers HTTP headers to be sent with the request for the file.
	 * @param file.flags Additional flags for appending stored files to the DOM or other operations
	 * @returns {Promise<void>} Resolves when the file is added and the file is appended to the document when flags are used.
	 * @throws {Error} If the `file` parameter is not an object.
	 * @throws {Error} If the `file` parameter does not contain a valid parameters.
	 * @since 1.0.0
	 *
	 * @example
	 * await sos.offline.addFile({
	 * 	uri: 'https://code.jquery.com/jquery-3.7.1.slim.min.js',
	 * 	uid: 'jquery-3.7.1.slim.min.js',
	 * 	type: sos.offline.types.javascript,
	 * 	flags: [sos.offline.flags.append(document.body)],
	 * });
	 */
	public async addFile(file: ISaveFile): Promise<void> {
		validate({ addFile: file }).required().object(VISaveFile);

		const savedFile = await this.cache.loadOrSaveFile(file.uid, file.uri, file.headers);

		const flags = file.flags || [];
		for (const flag of flags) {
			await this.handleFileFlag(flag, savedFile, file.type);
		}
	}

	/**
	 * The `addFont()` method downloads the specified file and stores it locally. You should use this method for loading fonts instead of
	 * using `addFile()`, because it also generates appropriate font-face definition.
	 *
	 * @param font.uid Unique file identifier is used for later file retrieval, must contain a-z,A-Z,0-9 and . characters.
	 * @param font.append Reference to HTMLElement where the generated font-face will resist.
	 * @param font.fontFamily Font family that can be referenced from your CSS.
	 * @param font.formats URI where these formats will be downloaded from.
	 * @param font.fontStretch Allows you to make text wider or narrower.
	 * @param font.fontStyle Specifies the font style for a text. (Either `normal`, `italic`, `oblique`, `initial` or `inherit`)
	 * @param font.fontWeight Sets how thick or thin characters in text should be displayed
	 * @param font.unicodeRange Defines the range of Unicode characters the font supports, default value is "U+0-10FFFF"
	 * @param font.formats Dictionary of supported formats with its files
	 * @throws {Error} If the `font` parameter is not an object.
	 * @returns {Promise<void>} Resolves when the font is added and the style element is appended to the document.
	 * @since 2.0.0
	 *
	 * @example // {@link https://github.com/signageos/applet-examples/tree/master/examples/content-js-api/fonts | Example of Applet that loads custom font}
	 */
	public async addFont(font: IAddFont): Promise<void> {
		validate({ addFont: font }).required().object(VIAddFont);
		const fontGenerator = fontFaceGenerator(this.window, font);

		const downloadingFonts = (Object.keys(font.formats) as (keyof IFontFormats)[]).map(async (fontExtension: keyof IFontFormats) => {
			const file = await this.cache.loadOrSaveFile(generateFontUid(font.uid, fontExtension), font.formats[fontExtension]!);
			fontGenerator.setFormat(fontExtension, file.filePath);
		});
		await Promise.all(downloadingFonts);

		const styleElement = fontGenerator.generateStyleElement();
		font.append.appendChild(styleElement);
	}

	private async handleFileFlag(flag: IFlag, file: IFile, fileType: IFileType) {
		const flagController = this.flagControllers[flag.type];
		if (typeof flagController === 'undefined') {
			throw new Error('Not implemented apply flag ' + flag.type);
		}
		const filePath = file.filePath;
		await flagController.applyFlag(flag, fileType, filePath);
	}

	private getMessagePrefix() {
		return this.messagePrefix + '.' + Offline.MESSAGE_PREFIX;
	}
}
